import React, { useState } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  AppBar,
  Toolbar,
  Radio,
  RadioGroup,
  LinearProgress,
} from '@mui/material';
import { Delete, Warning, Download, DeleteForever, InfoOutlined } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import { User } from 'firebase/auth';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import { watchlistService, movieCacheService, unifiedCollection } from '@watchlist/shared';
import { authService } from '../../services/authService';
import DeleteAccountDialog from '../dialogs/DeleteAccountDialog';
import CustomButton from '../widgets/CustomButton';
import { font } from '../../utils/fontHelper';
import { parseFirebaseDate } from '../../utils/dateUtils';
import { glassStyles } from '../../tokens/designTokens';
import * as XLSX from 'xlsx';
import AppBarContent from '../layouts/AppBarContent';

interface UserDataManagerProps {
  onBack: () => void;
  user: User;
}

type DownloadFormat = 'csv' | 'xlsx' | 'pdf';

// ── Date formatting ──────────────────────────────────────────────────────────

const fmtDate = (val: any): string => {
  if (!val) return '';
  try {
    const d = parseFirebaseDate(val);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

// ── Download builders ────────────────────────────────────────────────────────

const csvEscape = (v: string | number | undefined | null) =>
  `"${String(v ?? '').replace(/"/g, '""')}"`;

const buildCsv = (
  sections: Array<{ title: string; headers: string[]; rows: Array<Array<string | number | undefined | null>> }>
): string =>
  sections
    .map(s => [
      s.title,
      s.headers.map(csvEscape).join(','),
      ...s.rows.map(r => r.map(csvEscape).join(',')),
      '',
    ].join('\n'))
    .join('\n');

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// UTF-8 safe string → base64 (for Capacitor Filesystem)
const strToBase64 = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary);
};

// ── Main Component ───────────────────────────────────────────────────────────

const UserDataManager: React.FC<UserDataManagerProps> = ({ onBack, user }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isNative = Capacitor.isNativePlatform();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showDeleteDataDialog, setShowDeleteDataDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [confirmDeleteData, setConfirmDeleteData] = useState(false);
  const [deletingData, setDeletingData] = useState(false);

  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>('csv');
  const [generating, setGenerating] = useState(false);

  const {
    clearAllUserData,
    movieDataCache,
    isLoading: movieDataLoading,
  } = useUserMovieData();

  // ── Stats derived from cache ─────────────────────────────────────────────

  const stats = React.useMemo(() => {
    const all = Array.from(movieDataCache.values());
    const ratedMovies = all.filter(d => d.rating);
    const avgRating =
      ratedMovies.length > 0
        ? ratedMovies.reduce((s, d) => s + (d.rating ?? 0), 0) / ratedMovies.length
        : 0;
    return {
      wantToWatch: all.filter(d => d.isInWatchlist).length,
      watched: all.filter(d => d.isWatched).length,
      totalRatings: ratedMovies.length,
      totalReviews: all.filter(d => d.review).length,
      avgRating: Math.round(avgRating * 10) / 10,
    };
  }, [movieDataCache]);

  // ── Build download data (enriched from movie cache, on demand) ────────────

  const buildDownloadData = async () => {
    const all = Array.from(movieDataCache.values());
    const wantToWatchEntries = all.filter(d => d.isInWatchlist);
    const watchedEntries = all.filter(d => d.isWatched);

    const allMovieIds = [
      ...new Set([
        ...wantToWatchEntries.map(d => d.movieId),
        ...watchedEntries.map(d => d.movieId),
      ]),
    ];

    // Fetch enriched movie data (title, year, director, tmdbId) from movie cache
    const movieDetails = new Map<string, { tmdbId: string; title: string; year: string; director: string }>();
    try {
      const cacheData = await movieCacheService.getBatchMovieData(allMovieIds);
      cacheData.forEach(m => {
        movieDetails.set(String(m.id), {
          tmdbId: String(m.id),
          title: m.title || '',
          year: m.year || (m.releaseDate ? m.releaseDate.split('-')[0] : ''),
          director: (m.directors || []).map((d: any) => d.name).filter(Boolean).join(', '),
        });
      });
    } catch (e) {
      console.warn('UserDataManager: Could not fetch movie cache data:', e);
    }

    const lookup = (movieId: string) =>
      movieDetails.get(String(movieId)) ?? { tmdbId: movieId, title: '', year: '', director: '' };

    const wantToWatchRows = wantToWatchEntries
      .map(d => {
        const m = lookup(d.movieId);
        return { tmdbId: m.tmdbId, title: m.title, year: m.year, director: m.director, addedOn: fmtDate(d.createdAt), notes: d.notes ?? '' };
      })
      .sort((a, b) => a.title.localeCompare(b.title));

    const watchedRows = watchedEntries
      .map(d => {
        const m = lookup(d.movieId);
        return {
          tmdbId: m.tmdbId, title: m.title, year: m.year, director: m.director,
          rating: d.rating ? `${d.rating}/10` : '',
          review: d.review ?? '',
          watchedOn: fmtDate(d.watchedDate),
          updatedOn: fmtDate(d.updatedAt),
          notes: d.notes ?? '',
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));

    // Watchlists (owned + collaborated)
    const [ownedWatchlists, collaboratedDocs] = await Promise.all([
      watchlistService.getUserWatchlists(user.uid),
      unifiedCollection('watchlists')
        .where('collaborators', 'array-contains', user.uid)
        .getDocs()
        .catch(() => [] as any[]),
    ]);

    const collaboratedWatchlists = (collaboratedDocs as any[]).map((doc: any) => ({ id: doc.id, ...doc.data() }));

    const watchlistRows = [
      ...ownedWatchlists.map(wl => ({
        name: wl.name,
        description: wl.description ?? '',
        movieCount: (wl.movieDetails ?? []).length,
        type: 'Owned',
        movies: (wl.movieDetails ?? []).map((m: any) => m.title).filter(Boolean).join('; '),
        notes: (wl.movieDetails ?? []).filter((m: any) => m.note).map((m: any) => `${m.title}: ${m.note}`).join(' | '),
      })),
      ...collaboratedWatchlists.map((wl: any) => ({
        name: wl.name,
        description: wl.description ?? '',
        movieCount: (wl.movieDetails ?? []).length,
        type: 'Collaborated',
        movies: (wl.movieDetails ?? []).map((m: any) => m.title).filter(Boolean).join('; '),
        notes: (wl.movieDetails ?? []).filter((m: any) => m.note).map((m: any) => `${m.title}: ${m.note}`).join(' | '),
      })),
    ];

    return { wantToWatchRows, watchedRows, watchlistRows };
  };

  // ── Download handler ──────────────────────────────────────────────────────

  const handleDownload = async () => {
    setShowDownloadDialog(false);
    setGenerating(true);
    try {
      const { wantToWatchRows, watchedRows, watchlistRows } = await buildDownloadData();
      const dateStr = new Date().toISOString().split('T')[0];


      // ── Build shared data structures ────────────────────────────────────
      const buildWorkbook = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
          ['TMDB ID', 'Movie Name', 'Year', 'Director', 'Added On', 'Notes'],
          ...wantToWatchRows.map(r => [r.tmdbId, r.title, r.year, r.director, r.addedOn, r.notes]),
        ]), 'Want to Watch');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
          ['TMDB ID', 'Movie Name', 'Year', 'Director', 'Rating', 'Review', 'Watched On', 'Updated On', 'Notes'],
          ...watchedRows.map(r => [r.tmdbId, r.title, r.year, r.director, r.rating, r.review, r.watchedOn, r.updatedOn, r.notes]),
        ]), 'Watched');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
          ['Watchlist Name', 'Description', 'Movie Count', 'Type', 'Movies', 'Notes'],
          ...watchlistRows.map(r => [r.name, r.description, r.movieCount, r.type, r.movies, r.notes]),
        ]), 'Watchlists');
        return wb;
      };

      const buildCsvContent = () => buildCsv([
        { title: 'WANT TO WATCH', headers: ['TMDB ID', 'Movie Name', 'Year', 'Director', 'Added On', 'Notes'], rows: wantToWatchRows.map(r => [r.tmdbId, r.title, r.year, r.director, r.addedOn, r.notes]) },
        { title: 'WATCHED', headers: ['TMDB ID', 'Movie Name', 'Year', 'Director', 'Rating', 'Review', 'Watched On', 'Updated On', 'Notes'], rows: watchedRows.map(r => [r.tmdbId, r.title, r.year, r.director, r.rating, r.review, r.watchedOn, r.updatedOn, r.notes]) },
        { title: 'WATCHLISTS', headers: ['Watchlist Name', 'Description', 'Movie Count', 'Type', 'Movies', 'Notes'], rows: watchlistRows.map(r => [r.name, r.description, r.movieCount, r.type, r.movies, r.notes]) },
      ]);

      const buildPdfHtml = () => {
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const tbl = (headers: string[], rows: Array<Array<string | number>>) => `
          <table>
            <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
            <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c || '—'}</td>`).join('')}</tr>`).join('')}</tbody>
          </table>`;
        return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>My Watchlist Data</title><style>
          body{font-family:-apple-system,sans-serif;font-size:11px;color:#111;padding:24px}
          h1{font-size:20px;margin-bottom:4px}.meta{color:#666;margin-bottom:24px;font-size:10px}
          h2{font-size:14px;margin:24px 0 8px;border-bottom:1px solid #ddd;padding-bottom:4px}
          table{width:100%;border-collapse:collapse;margin-bottom:8px}
          th{background:#f0f0f0;text-align:left;padding:6px 8px;font-size:10px}
          td{padding:5px 8px;border-bottom:1px solid #eee;vertical-align:top;word-break:break-word}
          @media print{body{padding:0}}
        </style></head><body>
          <h1>My Watchlist Data</h1><p class="meta">Exported on ${date}</p>
          <h2>Want to Watch (${wantToWatchRows.length})</h2>
          ${tbl(['TMDB ID','Movie Name','Year','Director','Added On','Notes'], wantToWatchRows.map(r=>[r.tmdbId,r.title,r.year,r.director,r.addedOn,r.notes]))}
          <h2>Watched (${watchedRows.length})</h2>
          ${tbl(['TMDB ID','Movie Name','Year','Director','Rating','Watched On','Notes'], watchedRows.map(r=>[r.tmdbId,r.title,r.year,r.director,r.rating,r.watchedOn,r.notes]))}
          <h2>Watchlists (${watchlistRows.length})</h2>
          ${tbl(['Name','Description','Movie Count','Type'], watchlistRows.map(r=>[r.name,r.description,r.movieCount,r.type]))}
        </body></html>`;
      };

      if (isNative) {
        // ── Native: write to cache dir then open system share sheet ────────
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');

        let filename: string;
        let base64: string;

        if (downloadFormat === 'csv') {
          filename = `watchlist-data-${dateStr}.csv`;
          base64 = strToBase64(buildCsvContent());
        } else if (downloadFormat === 'xlsx') {
          filename = `watchlist-data-${dateStr}.xlsx`;
          base64 = XLSX.write(buildWorkbook(), { type: 'base64', bookType: 'xlsx' });
        } else {
          // PDF: share as HTML — user can open in browser and print/save as PDF
          filename = `watchlist-data-${dateStr}.html`;
          base64 = strToBase64(buildPdfHtml());
        }

        const written = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });

        await Share.share({
          title: 'My Watchlist Data',
          files: [written.uri],
          dialogTitle: 'Save or share your data',
        });

      } else {
        // ── Web: blob download ───────────────────────────────────────────────
        if (downloadFormat === 'csv') {
          triggerDownload(new Blob([buildCsvContent()], { type: 'text/csv;charset=utf-8;' }), `watchlist-data-${dateStr}.csv`);

        } else if (downloadFormat === 'xlsx') {
          XLSX.writeFile(buildWorkbook(), `watchlist-data-${dateStr}.xlsx`);

        } else {
          // PDF — print via hidden iframe to avoid popup blocker
          const html = buildPdfHtml();
          const iframe = document.createElement('iframe');
          iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0';
          document.body.appendChild(iframe);
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            iframeDoc.open();
            // eslint-disable-next-line no-restricted-syntax
            iframeDoc.write(html);
            iframeDoc.close();
            iframe.contentWindow?.focus();
            setTimeout(() => {
              iframe.contentWindow?.print();
              setTimeout(() => document.body.removeChild(iframe), 1000);
            }, 500);
          }
        }
      }

      setSuccess('Download ready');
    } catch (err) {
      console.error('UserDataManager: Download error:', err);
      setError('Failed to generate download. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Delete handlers ────────────────────────────────────────────────────────

  const handleDeleteAllData = async () => {
    try {
      setDeletingData(true);
      await clearAllUserData();
      await authService.deleteUserPreferences(user.uid);

      ['watchlist','watched','ratings','reviews',`movieData_${user.uid}`,
        'homeWatchlistViewMode','otherUserProfileViewMode','recentMovies','userWatchlists',
        'tmdb_cache','poster_cache','watchlist_posters_cache'].forEach(k => localStorage.removeItem(k));

      Object.keys(localStorage)
        .filter(k =>
          k.includes(user.uid) || k.startsWith('review_') ||
          k.startsWith('movie_') || k.startsWith('cache_') ||
          k.startsWith('tmdb_') || k.startsWith('poster_')
        )
        .forEach(k => localStorage.removeItem(k));

      setSuccess('All data deleted successfully');
      setShowDeleteDataDialog(false);
      setConfirmDeleteData(false);
    } catch (err) {
      console.error('Delete data error:', err);
      setError('Failed to delete data');
    } finally {
      setDeletingData(false);
    }
  };

  const handleAccountDeleted = () => {
    localStorage.clear();
    sessionStorage.clear();
    setSuccess('Account deleted successfully');
    setShowDeleteAccountDialog(false);
  };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (movieDataLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const popupSx = {
    ...glassStyles.popup(isDark),
    borderRadius: 3,
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', width: '100%', pb: 12 }}>

      {/* Generating overlay */}
      {generating && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 2000,
          bgcolor: 'rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        }}>
          <CircularProgress size={48} sx={{ color: '#fff' }} />
          <Typography sx={{ color: '#fff', fontFamily: font(), fontWeight: 600 }}>
            Preparing your data…
          </Typography>
          <LinearProgress sx={{ width: 200, borderRadius: 4 }} />
        </Box>
      )}

      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(28,28,30,0.6)' : 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.15)',
          pt: 'var(--safe-area-inset-top)',
        }}
      >
        <Toolbar disableGutters>
          <AppBarContent>
            <IconButton onClick={onBack} sx={{ mr: 2, color: 'text.primary' }}>
              <ArrowLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: isDark ? '#FFFFFF' : '#000000', fontFamily: font() }}>
              My Data
            </Typography>
          </AppBarContent>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box sx={{
        maxWidth: { xs: '100%', md: '600px' },
        mx: 'auto',
        pt: { xs: 'calc(var(--safe-area-inset-top) + 88px)', sm: 'calc(var(--safe-area-inset-top) + 96px)', md: 'calc(var(--safe-area-inset-top) + 104px)' },
        px: { xs: 2, md: 3 },
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}>

        {/* Privacy notice */}
        <Card sx={{ bgcolor: 'action.hover', borderRadius: 3, border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <InfoOutlined sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: font(), mb: 0.5 }}>
                  Your privacy choices
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  Download a copy of your data, delete your movie data while keeping your account, or permanently delete your account.{' '}
                  <Box component="a" href="/privacy.html" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Privacy Policy
                  </Box>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card sx={{ bgcolor: isDark ? '#2C2C2E' : '#F8F9FA', borderRadius: 3, border: `1px solid ${isDark ? '#3A3A3C' : '#E0E0E0'}` }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2.5 }}>
              {[
                { value: stats.wantToWatch, label: 'Want to Watch', color: 'primary.main' },
                { value: stats.watched, label: 'Watched', color: 'success.main' },
                { value: stats.totalRatings, label: 'Rated', color: 'warning.main' },
              ].map(({ value, label, color }) => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              {[
                { value: stats.totalReviews, label: 'Reviews', color: 'secondary.main' },
                { value: stats.avgRating || '—', label: 'Avg Rating', color: 'error.light' },
              ].map(({ value, label, color }) => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <CustomButton
              semantic="tertiary"
              variant="contained"
              icon={<Download />}
              label="Download My Data"
              disabled={generating}
              onClick={() => setShowDownloadDialog(true)}
              sx={{ width: '100%' }}
            />
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card sx={{ bgcolor: isDark ? '#2C2C2E' : '#FFF5F5', borderRadius: 3, border: `1px solid ${theme.palette.error.main}40` }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Warning sx={{ fontSize: 26, color: 'error.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main', fontFamily: font() }}>
                Danger Zone
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Remove all movie data, ratings, reviews, and preferences. Your account stays active.
                </Typography>
                <CustomButton
                  semantic="error"
                  variant="contained"
                  icon={<Delete />}
                  label="Delete All My Data"
                  onClick={() => setShowDeleteDataDialog(true)}
                  sx={{ width: '100%' }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Permanently delete your account and all associated data. This cannot be undone.
                </Typography>
                <CustomButton
                  semantic="error"
                  variant="contained"
                  icon={<DeleteForever />}
                  label="Delete My Account"
                  onClick={() => setShowDeleteAccountDialog(true)}
                  sx={{ width: '100%' }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

      </Box>

      {/* ── Download Format Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: popupSx }}
      >
        <DialogTitle sx={{ fontFamily: font(), fontWeight: 700, pb: 1 }}>
          Download My Data
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
            Your export includes three sections — Want to Watch, Watched, and Watchlists — with TMDB ID, title, year, director, ratings, reviews, and notes.
          </Typography>
          <RadioGroup
            value={downloadFormat}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDownloadFormat(e.target.value as DownloadFormat)}
          >
            {([
              { value: 'csv', label: 'CSV', desc: 'Comma-separated — opens in any spreadsheet app' },
              { value: 'xlsx', label: 'XLSX', desc: 'Excel workbook — one sheet per section' },
              { value: 'pdf', label: 'PDF', desc: isNative ? 'HTML file — open in Safari, then Share → Print to save as PDF' : 'Formatted document — save as PDF via browser print dialog' },
            ] as const).map(opt => (
              <Box
                key={opt.value}
                onClick={() => setDownloadFormat(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  border: `2px solid ${downloadFormat === opt.value ? theme.palette.primary.main : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                  cursor: 'pointer',
                  bgcolor: downloadFormat === opt.value ? `${theme.palette.primary.main}18` : 'transparent',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:last-child': { mb: 0 },
                }}
              >
                <Radio value={opt.value} size="small" sx={{ p: 0 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: font() }}>{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opt.desc}</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <CustomButton
            variant="text"
            semantic="secondary"
            label="Cancel"
            onClick={() => setShowDownloadDialog(false)}
          />
          <CustomButton
            semantic="brand"
            variant="contained"
            icon={<Download />}
            label="Download"
            onClick={handleDownload}
          />
        </DialogActions>
      </Dialog>

      {/* ── Delete Data Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={showDeleteDataDialog}
        onClose={() => setShowDeleteDataDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: popupSx }}
      >
        <DialogTitle sx={{ fontFamily: font(), fontWeight: 700, color: 'error.main', pb: 1 }}>
          Delete All Data
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
            This will permanently delete all your movie data including watchlists, watched movies, ratings, reviews, activity history, and preferences. Your account will remain active.
          </Typography>
          <Alert severity="error" sx={{ mb: 2.5, bgcolor: `${theme.palette.error.main}18`, border: `1px solid ${theme.palette.error.main}40` }}>
            This action cannot be undone.
          </Alert>
          <FormControlLabel
            control={<Switch checked={confirmDeleteData} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmDeleteData(e.target.checked)} color="error" />}
            label={
              <Typography variant="body2" sx={{ fontFamily: font() }}>
                I understand this action cannot be undone
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <CustomButton
            variant="text"
            semantic="secondary"
            label="Cancel"
            onClick={() => setShowDeleteDataDialog(false)}
          />
          <CustomButton
            semantic="error"
            variant="contained"
            icon={deletingData ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <Delete />}
            label={deletingData ? 'Deleting…' : 'Delete All Data'}
            disabled={!confirmDeleteData || deletingData}
            onClick={handleDeleteAllData}
          />
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <DeleteAccountDialog
        open={showDeleteAccountDialog}
        onClose={() => setShowDeleteAccountDialog(false)}
        user={user}
        onAccountDeleted={handleAccountDeleted}
      />

      {/* Toasts */}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>

    </Box>
  );
};

export default UserDataManager;
